import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
    getAllOrders,
    approveOrder,
    rejectOrder,
    getAllMemorials,
    deleteMemorial,
    getDashboardStats
} from '../api/admin';
import { logout } from '../auth';
import './AdminDashboard.css';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('pending');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State
    const [pendingOrders, setPendingOrders] = useState([]);
    const [orderHistory, setOrderHistory] = useState([]);
    const [memorials, setMemorials] = useState([]);
    const [stats, setStats] = useState({
        totalOrders: 0,
        approvedOrders: 0,
        pendingOrders: 0
    });

    // Fetch data on mount
    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);

            // Fetch stats
            const statsData = await getDashboardStats();
            setStats({
                totalOrders: statsData.totalOrders || 0,
                approvedOrders: statsData.approvedOrders || 0,
                pendingOrders: statsData.pendingOrders || 0
            });

            // Fetch all orders
            const allOrders = await getAllOrders();

            // Separate pending and completed orders
            const pending = allOrders.filter(order =>
                order.status === 'PENDING' || order.status === 'pending'
            );
            const history = allOrders.filter(order =>
                order.status === 'APPROVED' || order.status === 'REJECTED' ||
                order.status === 'approved' || order.status === 'rejected' ||
                order.status === 'DELIVERED' || order.status === 'delivered'
            );

            setPendingOrders(pending);
            setOrderHistory(history);

            // Fetch all memorials
            try {
                const memorialsData = await getAllMemorials();
                setMemorials(memorialsData || []);
            } catch (err) {
                console.error('Error fetching memorials:', err);
            }

        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (orderId) => {
        if (!window.confirm('Approve this order?')) return;

        try {
            await approveOrder(orderId);
            alert('Order approved successfully!');
            fetchDashboardData();
        } catch (err) {
            console.error('Error approving order:', err);
            alert('Failed to approve order: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleReject = async (orderId) => {
        const reason = window.prompt('Enter rejection reason:');
        if (!reason) return;

        try {
            await rejectOrder(orderId, reason);
            alert('Order rejected!');
            fetchDashboardData();
        } catch (err) {
            console.error('Error rejecting order:', err);
            alert('Failed to reject order: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleDeleteMemorial = async (memorialId, memorialName) => {
        if (!window.confirm(`Are you sure you want to delete the memorial for "${memorialName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await deleteMemorial(memorialId);
            alert('Memorial deleted successfully!');
            fetchDashboardData();
        } catch (err) {
            console.error('Error deleting memorial:', err);
            alert('Failed to delete memorial: ' + (err.response?.data?.message || err.message));
        }
    };

    const viewOrderDetails = (order) => {
        const details = `
Order Details:

Order Number: ${order.orderNumber || order.id}
Customer: ${order.customerName || `${order.deliveryFirstName} ${order.deliveryLastName}`}
Email: ${order.email || order.user?.email || 'N/A'}
Phone: ${order.phone || order.deliveryPhone || 'N/A'}
Template: ${order.templateType || 'N/A'}
Material: ${order.materialType || 'N/A'}
Plan: ${order.planType || 'N/A'}
Address: ${order.deliveryAddress || 'N/A'}
City: ${order.deliveryCity || 'N/A'}
Postal Code: ${order.deliveryPostalCode || 'N/A'}
        `.trim();

        alert(details);
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    // Loading state
    if (loading) {
        return (
            <div className="admin-dashboard">
                <div className="admin-particles">
                    <div className="particles-glow" />
                    {[...Array(20)].map((_, index) => (
                        <div key={index} className="particle" />
                    ))}
                </div>
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            {/* Background particles */}
            <div className="admin-particles">
                <div className="particles-glow" />
                {[...Array(20)].map((_, index) => (
                    <div key={index} className="particle" />
                ))}
            </div>

            {/* Main Container */}
            <div className="admin-container">
                <motion.div
                    className="admin-header"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <h1 className="admin-title">Admin Dashboard</h1>
                    <motion.button
                        className="logout-btn"
                        onClick={handleLogout}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Logout
                    </motion.button>
                </motion.div>

                {/* Error Message */}
                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                {/* Stats Cards */}
                <motion.div
                    className="stats-grid"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    <div className="stat-card">
                        <div className="stat-icon">📋</div>
                        <div className="stat-info">
                            <div className="stat-value">{stats.pendingOrders}</div>
                            <div className="stat-label">Pending Orders</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon">✅</div>
                        <div className="stat-info">
                            <div className="stat-value">{stats.approvedOrders}</div>
                            <div className="stat-label">Approved Orders</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon">🔲</div>
                        <div className="stat-info">
                            <div className="stat-value">{stats.totalOrders}</div>
                            <div className="stat-label">Total Orders</div>
                        </div>
                    </div>
                </motion.div>

                {/* Tabs */}
                <motion.div
                    className="admin-tabs"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                >
                    <button
                        className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
                        onClick={() => setActiveTab('pending')}
                    >
                        Pending Orders ({pendingOrders.length})
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        Order History ({orderHistory.length})
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'memorials' ? 'active' : ''}`}
                        onClick={() => setActiveTab('memorials')}
                    >
                        Manage Memorials ({memorials.length})
                    </button>
                </motion.div>

                {/* Content */}
                <motion.div
                    className="admin-content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                >
                    {activeTab === 'pending' && (
                        <div className="pending-orders">
                            {pendingOrders.length === 0 ? (
                                <div className="empty-state">
                                    <div className="empty-icon">📭</div>
                                    <p>No pending orders</p>
                                </div>
                            ) : (
                                pendingOrders.map((order) => (
                                    <motion.div
                                        key={order.id}
                                        className="order-card"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4 }}
                                    >
                                        <div className="order-header">
                                            <div className="order-number">
                                                ORD-{order.id}
                                            </div>
                                            <div className="order-date">
                                                {new Date(order.createdAt).toLocaleDateString()}
                                            </div>
                                        </div>

                                        <div className="order-body">
                                            <div className="order-info-grid">
                                                <div className="info-item">
                                                    <span className="info-label">Customer:</span>
                                                    <span className="info-value">
                                                        {order.deliveryFirstName} {order.deliveryLastName}
                                                    </span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="info-label">Phone:</span>
                                                    <span className="info-value">{order.deliveryPhone}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="info-label">City:</span>
                                                    <span className="info-value">{order.deliveryCity}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="info-label">Postal Code:</span>
                                                    <span className="info-value">{order.deliveryPostalCode}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="info-label">Template:</span>
                                                    <span className="info-value">{order.templateType}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="info-label">Material:</span>
                                                    <span className="info-value">{order.materialType}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="info-label">Plan:</span>
                                                    <span className="info-value">{order.planType}</span>
                                                </div>
                                                <div className="info-item">
                                                    <span className="info-label">Price:</span>
                                                    <span className="info-value">€{order.total}</span>
                                                </div>
                                            </div>

                                            <div className="delivery-address">
                                                <span className="info-label">Delivery Address:</span>
                                                <span className="info-value">{order.deliveryAddress}</span>
                                            </div>
                                        </div>

                                        <div className="order-actions">
                                            <button
                                                className="view-details-btn"
                                                onClick={() => viewOrderDetails(order)}
                                            >
                                                View Details
                                            </button>
                                            <div className="action-buttons">
                                                <motion.button
                                                    className="reject-btn"
                                                    onClick={() => handleReject(order.id)}
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                >
                                                    Reject
                                                </motion.button>
                                                <motion.button
                                                    className="approve-btn"
                                                    onClick={() => handleApprove(order.id)}
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                >
                                                    Approve
                                                </motion.button>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))
                            )}
                        </div>
                    )}

                    {activeTab === 'history' && (
                        <div className="order-history">
                            <div className="history-table">
                                <div className="table-header">
                                    <div className="th">Order ID</div>
                                    <div className="th">Customer</div>
                                    <div className="th">Template</div>
                                    <div className="th">Plan</div>
                                    <div className="th">Status</div>
                                    <div className="th">Date</div>
                                </div>
                                {orderHistory.length === 0 ? (
                                    <div className="empty-state-table">
                                        No order history yet
                                    </div>
                                ) : (
                                    orderHistory.map((item) => (
                                        <div key={item.id} className="table-row">
                                            <div className="td">ORD-{item.id}</div>
                                            <div className="td">
                                                {item.deliveryFirstName} {item.deliveryLastName}
                                            </div>
                                            <div className="td">{item.templateType}</div>
                                            <div className="td">{item.planType}</div>
                                            <div className="td">
                                                <span className={`status-badge ${item.status.toLowerCase()}`}>
                                                    {item.status}
                                                </span>
                                            </div>
                                            <div className="td">
                                                {new Date(item.updatedAt).toLocaleDateString()}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'memorials' && (
                        <div className="memorials-management">
                            {memorials.length === 0 ? (
                                <div className="empty-state">
                                    <div className="empty-icon">🪦</div>
                                    <p>No memorials created yet</p>
                                </div>
                            ) : (
                                <div className="memorials-grid">
                                    {memorials.map((memorial) => (
                                        <motion.div
                                            key={memorial.id}
                                            className="memorial-item"
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.4 }}
                                        >
                                            <div className="memorial-header">
                                                <h3 className="memorial-name">
                                                    {memorial.firstName} {memorial.lastName}
                                                </h3>
                                                <span className={`status-badge ${memorial.isPublished ? 'approved' : 'pending'}`}>
                                                    {memorial.isPublished ? 'Published' : 'Draft'}
                                                </span>
                                            </div>
                                            <div className="memorial-info">
                                                <p className="memorial-years">
                                                    {memorial.yearOfBirth} — {memorial.yearOfDeath}
                                                </p>
                                                <p className="memorial-code">QR Code: {memorial.code}</p>
                                                {memorial.location && (
                                                    <p className="memorial-location">📍 {memorial.location}</p>
                                                )}
                                            </div>
                                            <div className="memorial-actions">
                                                <button
                                                    className="view-memorial-btn"
                                                    onClick={() => window.open(`/q/${memorial.code}`, '_blank')}
                                                >
                                                    View Memorial
                                                </button>
                                                <motion.button
                                                    className="delete-memorial-btn"
                                                    onClick={() => handleDeleteMemorial(memorial.id, `${memorial.firstName} ${memorial.lastName}`)}
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                >
                                                    Delete
                                                </motion.button>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </motion.div>
            </div>
        </div>
    );
};

export default AdminDashboard;
